import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Clock } from 'lucide-react'
import { getInventoryItems, postValuationWriteDown, previewWriteDownAll, previewWriteDownSome, resolveProductByCode, reverseValuation } from '../../api/inventory'
import ErrorBanner from '../../components/ErrorBanner'
import { BackLink, DocHeader } from '../../components/PageParts'
import { docNo } from '../../utils/docNumbers'
import { useSession } from '../../session'
import { money, num } from '../sales/salesShared'
import BinScan from './BinScan'
import ItemPicker from './ItemPicker'
import { binLabel, qty, stockItems, unitCost, useAllAreas, useBins, useItemBins, useLocations } from './inventoryShared'

// What happened to the stock, in plain words, and how each maps onto the
// valuation API: shrinkage and overage change the count and post a loss or
// gain; "worth less" writes the cost down; "worth more" reverses that.
const KINDS = [
  { id: 'damaged', label: 'Damaged', hint: 'Broken or spoiled', reason: 'damage', counts: -1 },
  { id: 'lost', label: 'Lost or stolen', hint: 'Missing from the shelf', reason: 'theft', counts: -1 },
  { id: 'found', label: 'Found extra', hint: 'More than the count', reason: 'overage', counts: 1 },
  { id: 'less', label: 'Worth less now', hint: 'Old, out of date, cheaper now' },
  { id: 'more', label: 'Worth more again', hint: 'Undo an earlier write-down', reversal: true },
]

export default function AdjustStock() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const { roles, binTracking } = useSession()
  const seesBooks = roles.includes('admin') || roles.includes('accounting') || roles.includes('accountant')
  const locations = useLocations()
  const [items, setItems] = useState([])
  const [productId, setProductId] = useState(params.get('product_id') || '')
  const [detail, setDetail] = useState(null) // from product-by-code: prev_avg_cost, nrv_qty
  const [kind, setKind] = useState('damaged')
  const [count, setCount] = useState('')
  const [value, setValue] = useState('')
  const [locationId, setLocationId] = useState('')
  const [askApproval, setAskApproval] = useState(false)
  const [preview, setPreview] = useState(null)
  const [areaId, setAreaId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(null)

  useEffect(() => { getInventoryItems().then((d) => setItems(stockItems(d))).catch(() => setItems([])) }, [])
  useEffect(() => { if (locations?.length && !locationId) setLocationId(String(locations[0].location_id)) }, [locations, locationId])

  const item = items.find((p) => String(p.product_id) === String(productId))
  const k = KINDS.find((x) => x.id === kind)
  const onHand = num(item?.qty_on_hand)
  const cost = unitCost(item)
  const writtenDown = num(detail?.nrv_qty) > 0
  const kinds = KINDS.filter((x) => !x.reversal || writtenDown)

  // With bin tracking, damage and losses come out of a bin that holds the
  // item; found stock can go into any bin at the location.
  const useBinsHere = binTracking && Boolean(k.counts)
  const allBins = useBins(useBinsHere ? locationId : '')
  const itemBins = useItemBins(useBinsHere ? item?.sku : '')
  const holding = useMemo(() => (itemBins?.rows || []).filter((r) => String(r.location_id) === String(locationId) && num(r.qty) > 0), [itemBins, locationId])
  const binChoices = k.counts < 0 ? holding.map((r) => ({ area_id: r.area_id, label: `${r.path} · ${qty(r.qty)}`, qty: num(r.qty), path: r.path })) : allBins.map((b) => ({ area_id: b.area_id, label: binLabel(b), path: b.path }))
  const bin = binChoices.find((b) => String(b.area_id) === String(areaId))
  const allAreas = useAllAreas()
  const wantArea = useRef('') // a scanned bin at another location, until its bins load
  useEffect(() => {
    if (!useBinsHere) { setAreaId(''); return }
    const wanted = binChoices.find((b) => String(b.area_id) === String(wantArea.current))
    if (wanted) { setAreaId(String(wanted.area_id)); wantArea.current = ''; return }
    setAreaId((cur) => (binChoices.some((b) => String(b.area_id) === String(cur)) ? cur : (binChoices.length === 1 ? String(binChoices[0].area_id) : '')))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBinsHere, kind, locationId, holding, allBins])

  // Cost history for "worth less/more" comes from the code lookup.
  useEffect(() => {
    setDetail(null)
    if (!item?.sku) return
    resolveProductByCode(item.sku).then((d) => setDetail(d || null)).catch(() => setDetail(null))
  }, [item?.sku])
  useEffect(() => { if (kind === 'more' && !writtenDown) setKind('damaged') }, [kind, writtenDown])

  // What a write-down would cost, before recording it.
  const writeDownAll = kind === 'less' && (!String(count).trim() || num(count) >= onHand)
  useEffect(() => {
    setPreview(null)
    if (kind !== 'less' || !item || String(value).trim() === '' || !Number.isFinite(Number(value))) return undefined
    const t = setTimeout(() => {
      const req = writeDownAll
        ? previewWriteDownAll({ product_id: item.product_id, nrv: Number(value) })
        : previewWriteDownSome({ product_id: item.product_id, qty: num(count), nrv: Number(value) })
      req.then((p) => setPreview(p || null)).catch(() => setPreview(null))
    }, 300)
    return () => clearTimeout(t)
  }, [kind, item, value, count, writeDownAll])

  const effect = (() => {
    if (!item) return ''
    if (k.counts) {
      const n = num(count)
      if (n <= 0) return ''
      const amount = money(n * cost)
      const where = bin ? ` ${k.counts < 0 ? 'from' : 'in'} ${bin.path}` : ''
      return k.counts < 0
        ? `Takes ${qty(n)} out of stock${where} (${qty(Math.max(0, onHand - n))} left) and records a ${amount} loss.`
        : `Adds ${qty(n)} to stock${where} (${qty(onHand + n)} on hand) and records a ${amount} gain.`
    }
    if (kind === 'less' && preview) {
      return num(preview.total_loss) > 0
        ? `Lowers the value of ${writeDownAll ? `all ${qty(onHand)}` : qty(count)} and records a ${money(preview.total_loss)} loss. The count doesn’t change.`
        : 'Nothing to write down: they already cost this much or less.'
    }
    return ''
  })()

  const save = async () => {
    setError('')
    if (!item) { setError('Choose the product.'); return }
    try {
      let res
      if (k.counts) {
        const n = num(count)
        if (!(n > 0)) { setError('Enter how many.'); return }
        if (k.counts < 0 && n > onHand + 1e-9) { setError(`Only ${qty(onHand)} are on hand.`); return }
        if (cost <= 0) { setError('This product has no cost yet, so there’s nothing to record. Receive it first.'); return }
        if (useBinsHere && !bin) { setError(k.counts < 0 ? (holding.length ? 'Choose the bin it came out of.' : 'This product isn’t in any bin at this location.') : 'Choose the bin it was found in.'); return }
        if (bin?.qty != null && n > bin.qty + 1e-9) { setError(`Only ${qty(bin.qty)} are in ${bin.path}.`); return }
        setSaving(true)
        res = await postValuationWriteDown({ product_id: item.product_id, reason: k.reason, qty: n, location_id: locationId ? Number(locationId) : undefined, area_id: bin ? Number(bin.area_id) : undefined, require_approval: askApproval || undefined })
      } else if (kind === 'less') {
        if (String(value).trim() === '' || !(Number(value) >= 0)) { setError('Enter what each one is worth now.'); return }
        if (Number(value) >= cost) { setError(`That’s not less than they cost (${money(cost)} each).`); return }
        if (String(count).trim() && !(num(count) > 0)) { setError('Enter how many, or leave it blank for all of them.'); return }
        if (preview && num(preview.total_loss) <= 0) { setError('Nothing to write down at that value.'); return }
        setSaving(true)
        res = writeDownAll
          ? await postValuationWriteDown({ product_id: item.product_id, reason: 'nrv', nrv: Number(value), require_approval: askApproval || undefined })
          : await postValuationWriteDown({ product_id: item.product_id, reason: 'deterioration', qty: num(count), nrv: Number(value), require_approval: askApproval || undefined })
      } else {
        if (!(Number(value) > cost)) { setError(`Enter a value above what they’re carried at now (${money(cost)} each).`); return }
        setSaving(true)
        res = await reverseValuation({ product_id: item.product_id, new_nrv: Number(value), require_approval: askApproval || undefined })
      }
      setDone({ ...res, kind: k.label, item })
    } catch (e) {
      setError(e.message || 'Could not record this.')
    } finally {
      setSaving(false)
    }
  }

  const reset = () => { setDone(null); setCount(''); setValue(''); setAskApproval(false) }

  if (done) {
    const pending = String(done.status || '').toLowerCase() === 'draft'
    return (
      <div className="ci-form-page">
        <DocHeader back={<BackLink to="/inventory/stock" label="Stock" />} title={pending ? 'Sent for approval' : 'Stock adjusted'} />
        <section className="doc-card doc-done">
          {pending ? <Clock size={28} aria-hidden="true" /> : <CheckCircle2 size={28} aria-hidden="true" />}
          <div>
            <h2>{done.kind}: {done.item.name || done.item.sku}</h2>
            <p>
              {pending
                ? `An admin needs to approve this ${money(done.amount)} adjustment before the stock changes.`
                : `${money(done.amount)} recorded${done.new_qoh != null ? `; ${qty(done.new_qoh)} now on hand` : ''}.`}
              {seesBooks && done.je_id && <> Journal entry <Link to={`/accounting/journal/${done.je_id}`}>{docNo('je', done.je_no)}</Link>.</>}
            </p>
          </div>
          <div className="doc-done-actions">
            <button type="button" className="ci-btn ci-btn-ghost" onClick={reset}>Adjust another</button>
            <Link className="ci-btn ci-btn-primary" to={`/inventory/stock/${done.item.product_id}`}>View product</Link>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="ci-form-page">
      <DocHeader back={<BackLink to="/inventory/stock" label="Stock" />} title="Adjust stock" meta="Record stock that was damaged, lost, found, or is worth less than it cost." />

      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card">
        <ItemPicker items={items} value={productId} onChange={setProductId} />
        {item && <p className="doc-hint">{qty(onHand)} on hand at {money(cost)} each.</p>}
      </div>

      <div className="segmented wrap" role="radiogroup" aria-label="What happened?">
        {kinds.map((x) => (
          <button key={x.id} type="button" role="radio" aria-checked={kind === x.id} className={kind === x.id ? 'active' : ''} onClick={() => { setKind(x.id); setPreview(null) }}>
            {x.label}<small>{x.hint}</small>
          </button>
        ))}
      </div>

      <div className="ci-form-meta-card">
        <div className={`ci-form-meta-top ${useBinsHere && locations?.length > 1 ? 'three' : 'two'}`}>
          {kind !== 'more' && (
            <label className="ci-form-field">
              <span>{kind === 'less' ? 'How many (leave blank for all)' : 'How many'}</span>
              <input value={count} onChange={(e) => setCount(e.target.value)} inputMode="decimal" placeholder={kind === 'less' ? `All ${qty(onHand)}` : '0'} />
            </label>
          )}
          {k.counts && locations?.length > 1 && (
            <label className="ci-form-field">
              <span>{k.counts < 0 ? 'Taken from' : 'Found at'}</span>
              <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
              </select>
            </label>
          )}
          {useBinsHere && (
            <div className="inv-bin-field">
              <label className="ci-form-field">
                <span>{k.counts < 0 ? 'From bin' : 'Into bin'}</span>
                <select value={areaId} onChange={(e) => setAreaId(e.target.value)} disabled={!item}>
                  <option value="">{!item ? 'Choose the product first' : (binChoices.length ? 'Choose a bin…' : (k.counts < 0 ? 'Not in any bin here' : 'No bins here yet'))}</option>
                  {binChoices.map((b) => <option key={b.area_id} value={b.area_id}>{b.label}</option>)}
                </select>
              </label>
              {item && (
                <BinScan
                  areas={allAreas}
                  locationId={locationId}
                  placeholder="Or scan the bin label"
                  prefer={(a) => k.counts > 0 || (itemBins?.rows || []).some((r) => String(r.area_id) === String(a.area_id) && num(r.qty) > 0)}
                  validate={(a) => {
                    if (allAreas.some((x) => x.level === 'Bin') && a.level !== 'Bin') return `That’s ${a.level === 'Aisle' ? 'an' : 'a'} ${a.level.toLowerCase()} label (${a.path}). Scan a bin.`
                    if (k.counts < 0 && !(itemBins?.rows || []).some((r) => String(r.area_id) === String(a.area_id) && num(r.qty) > 0)) return `${item.name || item.sku} isn’t in ${a.path}.`
                    return ''
                  }}
                  onPick={(a) => {
                    if (String(a.location_id) === String(locationId)) { setAreaId(String(a.area_id)); return }
                    wantArea.current = String(a.area_id)
                    setLocationId(String(a.location_id))
                  }}
                />
              )}
            </div>
          )}
          {!k.counts && (
            <label className="ci-form-field">
              <span>Worth now, each</span>
              <input value={value} onChange={(e) => setValue(e.target.value)} inputMode="decimal" placeholder={money(cost)} />
            </label>
          )}
        </div>
        {kind === 'more' && detail && <p className="doc-hint">Written down from {money(detail.prev_avg_cost)} to {money(cost)}. Enter up to {money(detail.prev_avg_cost)}.</p>}
        {kind === 'less' && preview && (preview.segments || []).length > 0 && (
          <ul className="doc-hint inv-preview">
            {preview.segments.map((s, i) => <li key={i}>{qty(s.qty)} at {money(s.from_cost)} → {money(s.to_cost)}: {money(s.loss)} loss</li>)}
          </ul>
        )}
        {effect && <p className="doc-note">{effect}</p>}
        <label className="ci-form-toggle">
          <input type="checkbox" checked={askApproval} onChange={(e) => setAskApproval(e.target.checked)} />
          Ask an admin to approve this first
        </label>
      </div>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving || !item} onClick={save}>{saving ? 'Saving…' : (askApproval ? 'Send for approval' : 'Record adjustment')}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav(-1)}>Cancel</button>
        </div>
      </div>

    </div>
  )
}
